'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * What the app says back when you do something.
 *
 * Every page had invented its own: a Notice pinned under a heading here, a
 * sentence beside a button there, and a fair few actions that said nothing at
 * all. Worse, they mostly said "Saved." A person who just changed a plan, a
 * number and a note wants to know which of the three the app took.
 *
 * So: one place, and messages that name what changed. "Plan changed to Pro" is
 * a receipt; "Saved" is a shrug.
 *
 * The one thing a message must never carry is a password or a key. Whoever
 * reads this panel over a shoulder, or in a screenshot pasted into a chat, is
 * not necessarily the person who was allowed to see the value.
 */

export type ToastTone = 'ok' | 'bad' | 'info';

/**
 * A way back out of what just happened, for as long as the toast is up. For an
 * action that really happened and can really be reversed — not a five-second
 * delay, which leaves a closed tab having reported a change nobody made.
 */
export interface ToastUndo {
  /** What the button says. "Undo" unless the reverse has its own word. */
  label?: string;
  /** Run when it is pressed. Failures are the caller's to report. */
  run: () => void | Promise<void>;
}

interface Toast {
  id: number;
  tone: ToastTone;
  text: string;
  undo?: ToastUndo;
  /** When this toast goes, so the button can count down to it. */
  until?: number;
}

interface ToastApi {
  /** Something worked. Say what it did. */
  ok: (text: string, undo?: ToastUndo) => void;
  /** Something failed. Say what was refused, in the words the server used. */
  bad: (text: string) => void;
  /** Neither; a fact worth surfacing. */
  info: (text: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const LIFETIME = { ok: 4000, info: 5000, bad: 8000 } as const;

/**
 * How long an undo stands. Five seconds is the number the manager asked for,
 * and it is the right shape: long enough to catch the click you did not mean,
 * short enough that nobody treats it as a draft state.
 */
const UNDO_MS = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (tone: ToastTone, text: string, undo?: ToastUndo) => {
      const id = next.current++;
      const life = undo ? UNDO_MS : LIFETIME[tone];
      // Three at a time. A fourth means the newest is what matters.
      setToasts((current) => [
        ...current.slice(-2),
        { id, tone, text, undo, until: undo ? Date.now() + life : undefined },
      ]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), life),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      ok: (text, undo) => push('ok', text, undo),
      bad: (text) => push('bad', text),
      info: (text) => push('info', text),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toasts" role="region" aria-label="Notifications">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.tone}`}
            // A failure interrupts; a confirmation waits its turn.
            role={toast.tone === 'bad' ? 'alert' : 'status'}
          >
            {/*
              No onClick on the toast itself. A click anywhere used to dismiss
              it, which fires on mouseup at the end of a text-selection drag
              same as any other click -- so trying to copy the message was
              the one gesture guaranteed to delete it first. The × is the
              only way to dismiss by hand now; the text stays selectable.
            */}
            <span className="selectable">{toast.text}</span>
            {toast.undo && (
              <UndoButton
                undo={toast.undo}
                until={toast.until ?? Date.now()}
                onDone={() => dismiss(toast.id)}
              />
            )}
            <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => dismiss(toast.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Outside a provider this returns a no-op rather than throwing: a component
 * that reports what it did should not be the reason a page fails to render.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  return api ?? FALLBACK;
}

const FALLBACK: ToastApi = { ok: () => {}, bad: () => {}, info: () => {} };

/**
 * The way back, with the seconds left on it. The count is drawn rather than
 * implied: a button that stops working in a moment and does not say so is one
 * people reach for too late.
 */
function UndoButton({
  undo,
  until,
  onDone,
}: {
  undo: ToastUndo;
  until: number;
  onDone: () => void;
}) {
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((until - Date.now()) / 1000)));

  useEffect(() => {
    const tick = setInterval(
      () => setLeft(Math.max(0, Math.ceil((until - Date.now()) / 1000))),
      250,
    );
    return () => clearInterval(tick);
  }, [until]);

  return (
    <button
      type="button"
      className="toast-undo"
      onClick={() => {
        // Dismissed first: the reverse is a request of its own and reports
        // itself, and leaving the receipt for the thing being undone on screen
        // beside it reads as two states at once.
        onDone();
        void undo.run();
      }}
    >
      {`${undo.label ?? 'Undo'}${left > 0 ? ` (${left})` : ''}`}
    </button>
  );
}
